import { useState } from 'react';

interface ResultItem {
  id: number;
  before: string;
  after: string;
  duration: string;
  description: string;
}

interface VideoItem {
  id: number;
  thumb: string;
  embedUrl: string;
  label: string;
  description: string;
}

interface LightboxState {
  open: boolean;
  resultId: number;
  side: 'before' | 'after';
}

const videos: VideoItem[] = [
  {
    id: 1,
    thumb: "/img/pre1.png",
    embedUrl: "https://www.youtube.com/embed/Ytl41_hWl3Q?autoplay=1&rel=0",
    label: 'Remodelação glútea com melhora de projeção',
    description: 'Resultado real — clique para assistir',
  },
  {
    id: 2,
    thumb: "/img/pre.png",
    embedUrl: "https://www.youtube.com/embed/Af70Cg7T2XU?autoplay=1&rel=0",
    label: 'Remodelação glútea com melhora de projeção',
    description: 'Resultado real — clique para assistir',
  },
];

export default function Results() {
  const [lightbox, setLightbox] = useState<LightboxState>({ open: false, resultId: 1, side: 'before' });
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);

  const results: ResultItem[] = [
    {
      id: 1,
      before: "/img/aantes1.png",
      after: "/img/ddepois1.png",
      duration: "Resultado imediato",
      description: "Remodelação glútea com melhora de projeção"
    },
    {
      id: 2,
      before: "/img/aantes2.png",
      after: "/img/ddepois2.png",
      duration: "Resultado imediato",
      description: "Remodelação glútea com melhora de projeção"
    },
    {
      id: 3,
      before: "/img/aantes3.png",
      after: "/img/ddepois3.png",
      duration: "Resultado imediato",
      description: "Remodelação glútea com melhora de projeção"
    },
    {
      id: 4,
      before: "/img/aantes4.png",
      after: "/img/ddepois4.png",
      duration: "Resultado imediato",
      description: "Remodelação glútea com melhora de projeção"
    },
    {
      id: 5,
      before: "/img/antes1.png",
      after: "/img/depois1.png",
      duration: "Resultado imediato",
      description: "Remodelação glútea com melhora de projeção"
    },
    {
      id: 6,
      before: "/img/antes2.png",
      after: "/img/depois2.png",
      duration: "Resultado imediato",
      description: "Toxina botulínica"
    },
    {
      id: 7,
      before: "/img/antes3.png",
      after: "/img/depois3.png",
      duration: "Resultado imediato",
      description: "Preenchimento labial com ácido hialurônico"
    },
    {
      id: 8,
      before: "/img/antes4.png",
      after: "/img/depois4.png",
      duration: "Resultado imediato",
      description: "Subcisão avançada, uma técnica que libera essas fibroses profundas e permite que a pele volte à sua posição natural."
    }
  ];

  const currentResult = results.find(r => r.id === lightbox.resultId)!;
  const currentIndex = results.findIndex(r => r.id === lightbox.resultId);

  const openLightbox = (resultId: number, side: 'before' | 'after') => {
    setLightbox({ open: true, resultId, side });
  };

  const closeLightbox = () => {
    setLightbox(prev => ({ ...prev, open: false }));
  };

  const navigateResult = (dir: 'prev' | 'next') => {
    const newIndex =
      dir === 'prev'
        ? (currentIndex - 1 + results.length) % results.length
        : (currentIndex + 1) % results.length;
    setLightbox(prev => ({ ...prev, resultId: results[newIndex].id }));
  };

  const toggleSide = () => {
    setLightbox(prev => ({ ...prev, side: prev.side === 'before' ? 'after' : 'before' }));
  };

  return (
    <section id="resultados" className="bg-[#faf8f5] py-20 px-5 sm:px-8 lg:px-16">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-px bg-[#C9A84C]"></div>
            <span
              className="text-[#C9A84C] text-xs tracking-[0.25em] uppercase font-semibold"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Galeria
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Resultados <span className="italic text-[#C9A84C]">Reais!</span>
          </h2>
        </div>

        {/* ── Resultados em Vídeo ── */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-5 h-px bg-[#e8dfc8]"></div>
            <span
              className="text-[#999] text-xs tracking-[0.2em] uppercase font-medium"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Vídeos
            </span>
            <div className="flex-1 h-px bg-[#e8dfc8]"></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {videos.map(video => (
              <div
                key={video.id}
                className="relative rounded-2xl overflow-hidden bg-white border border-[#e8dfc8] cursor-pointer group hover:shadow-lg transition-shadow duration-300"
                onClick={() => setActiveVideo(video)}
                role="button"
                aria-label={`Assistir vídeo: ${video.label}`}
              >
                {/* Thumbnail vertical */}
                <div className="relative overflow-hidden" style={{ aspectRatio: '9/16' }}>
                  <img
                    src={video.thumb}
                    alt={video.label}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/35 transition-colors duration-300" />

                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 shadow-xl group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-play-fill text-[#C9A84C] text-xl ml-0.5"></i>
                    </div>
                  </div>

                  {/* Badge */}
                  <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/55 backdrop-blur-sm px-2 py-0.5 rounded-full">
                    <i className="ri-video-line text-white text-xs"></i>
                    <span
                      className="text-white text-xs font-medium"
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      Vídeo
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="px-3 py-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0"></div>
                    <span
                      className="text-[#C9A84C] text-xs font-semibold truncate"
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {video.label}
                    </span>
                  </div>
                  <p
                    className="text-[#888] text-xs leading-snug"
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    {video.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Divisor fotos */}
        <div className="flex items-center gap-3 mb-7">
          <div className="w-5 h-px bg-[#e8dfc8]"></div>
          <span
            className="text-[#999] text-xs tracking-[0.2em] uppercase font-medium"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Fotos antes &amp; depois
          </span>
          <div className="flex-1 h-px bg-[#e8dfc8]"></div>
        </div>

        {/* Grid de cards fotos */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {results.map(result => (
            <div
              key={result.id}
              className="rounded-2xl overflow-hidden bg-white border border-[#e8dfc8] shadow-sm hover:shadow-lg transition-shadow duration-300"
            >
              <div className="grid grid-cols-2 gap-1.5 p-3">
                <div
                  className="relative rounded-xl overflow-hidden cursor-pointer group"
                  style={{ aspectRatio: '4/5' }}
                  onClick={() => openLightbox(result.id, 'before')}
                >
                  <img
                    src={result.before}
                    alt="Antes"
                    className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-xl flex items-center justify-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-zoom-in-line text-[#C9A84C] text-base"></i>
                    </div>
                  </div>
                  <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full shadow-sm">
                    <span className="text-[#555] text-xs font-semibold" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      Antes
                    </span>
                  </div>
                </div>
                <div
                  className="relative rounded-xl overflow-hidden cursor-pointer group"
                  style={{ aspectRatio: '4/5' }}
                  onClick={() => openLightbox(result.id, 'after')}
                >
                  <img
                    src={result.after}
                    alt="Depois"
                    className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-xl flex items-center justify-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-zoom-in-line text-[#C9A84C] text-base"></i>
                    </div>
                  </div>
                  <div className="absolute top-2 left-2 bg-[#C9A84C] px-2 py-0.5 rounded-full shadow-sm">
                    <span className="text-white text-xs font-bold" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      Depois
                    </span>
                  </div>
                </div>
              </div>
              <div className="px-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]"></div>
                  <span className="text-[#C9A84C] text-xs font-semibold" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {result.duration}
                  </span>
                </div>
                <p className="text-[#666] text-sm leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {result.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Legal notice */}
        <div className="flex items-start gap-2 mt-8 px-1">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-[#C9A84C] text-sm"></i>
          </div>
          <p className="text-[#aaa] text-xs leading-relaxed italic" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Resultados são individuais e podem variar de paciente para paciente.
          </p>
        </div>
      </div>

      {/* ── Modal de Vídeo Vertical ── */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative bg-black rounded-3xl overflow-hidden shadow-2xl"
            style={{ width: '100%', maxWidth: 360 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={() => setActiveVideo(null)}
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>

            {/* Iframe 9:16 */}
            <div style={{ position: 'relative', paddingBottom: '177.78%', height: 0 }}>
              <iframe
                src={activeVideo.embedUrl}
                title={activeVideo.label}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Lightbox de fotos */}
      {lightbox.open && currentResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-sm w-full bg-white rounded-3xl overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-white/90 rounded-full shadow-md hover:bg-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-[#1a1a1a] text-lg"></i>
            </button>
            <div className="relative w-full" style={{ aspectRatio: '4/5' }}>
              <img
                src={lightbox.side === 'before' ? currentResult.before : currentResult.after}
                alt={lightbox.side === 'before' ? 'Antes' : 'Depois'}
                className="w-full h-full object-cover object-top"
              />
              <div
                className={`absolute top-4 left-4 px-3 py-1 rounded-full shadow ${lightbox.side === 'after' ? 'bg-[#C9A84C]' : 'bg-white/90'
                  }`}
              >
                <span
                  className={`text-sm font-bold ${lightbox.side === 'after' ? 'text-white' : 'text-[#555]'}`}
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {lightbox.side === 'before' ? 'Antes' : 'Depois'}
                </span>
              </div>
            </div>
            <div className="px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-[#C9A84C] text-xs font-semibold mb-0.5" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {currentResult.duration}
                </p>
                <p className="text-[#555] text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {currentResult.description}
                </p>
              </div>
              <button
                onClick={toggleSide}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#faf8f5] border border-[#e8dfc8] rounded-full text-xs font-semibold text-[#C9A84C] hover:border-[#C9A84C] transition-colors cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                <i className="ri-arrow-left-right-line text-sm"></i>
                {lightbox.side === 'before' ? 'Ver Depois' : 'Ver Antes'}
              </button>
            </div>
            <div className="flex items-center justify-between px-5 pb-4">
              <button
                onClick={() => navigateResult('prev')}
                className="flex items-center gap-1.5 text-xs text-[#aaa] hover:text-[#C9A84C] transition-colors cursor-pointer"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                <i className="ri-arrow-left-s-line text-base"></i>
                Anterior
              </button>
              <span className="text-xs text-[#ccc]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {currentIndex + 1} / {results.length}
              </span>
              <button
                onClick={() => navigateResult('next')}
                className="flex items-center gap-1.5 text-xs text-[#aaa] hover:text-[#C9A84C] transition-colors cursor-pointer"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Próximo
                <i className="ri-arrow-right-s-line text-base"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
